import { useState } from 'react'
import { Search, X } from 'lucide-react'
import type { Language } from '@/types'

interface SearchBarProps {
  query: string
  onQueryChange: (query: string) => void
  onClear: () => void
  resultCount?: number
  language: Language
  isRTL?: boolean
}

export function SearchBar({
  query,
  onQueryChange,
  onClear,
  resultCount,
  language,
  isRTL = false,
}: SearchBarProps) {
  const [focused, setFocused] = useState(false)

  const placeholder =
    language === 'fr'
      ? 'Rechercher un plat...'
      : language === 'ar'
      ? 'البحث عن طبق...'
      : 'Search for a dish...'

  const resultsText =
    resultCount !== undefined
      ? `${resultCount} ${
          language === 'fr'
            ? 'résultat' + (resultCount > 1 ? 's' : '')
            : language === 'ar'
            ? 'نتائج'
            : 'result' + (resultCount > 1 ? 's' : '')
        }`
      : ''

  return (
    <div className={`relative ${isRTL ? 'rtl' : ''}`}>
      <div
        className={`
          flex items-center gap-2.5 px-4 py-2.5 rounded-2xl
          bg-white border transition-all duration-200
          ${focused
            ? 'border-primary/50 ring-4 ring-primary/10 shadow-warm'
            : 'border-border hover:border-primary/30'}
        `}
      >
        {/* Search Icon */}
        <Search
          size={20}
          strokeWidth={2.25}
          className={`flex-shrink-0 transition-colors ${focused ? 'text-primary' : 'text-muted-foreground'}`}
        />

        {/* Input */}
        <input
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          className="flex-1 min-w-0 bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
        />

        {/* Clear Button */}
        {query && (
          <button
            onClick={onClear}
            className="p-1 rounded-full hover:bg-secondary/50 transition-colors touch-feedback"
            aria-label="Clear search"
          >
            <X size={18} className="text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Results Count — flows inline so it never overlaps content */}
      {query && resultsText && (
        <div className={`mt-1.5 text-xs text-muted-foreground ${isRTL ? 'text-right' : ''}`}>
          {resultsText}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Compact Search Bar (for smaller screens)
// ============================================================================

interface CompactSearchBarProps {
  query: string
  onQueryChange: (query: string) => void
  onClear: () => void
  language: Language
  isRTL?: boolean
  onFocus?: () => void
}

export function CompactSearchBar({
  query,
  onQueryChange,
  onClear,
  language,
  isRTL = false,
  onFocus,
}: CompactSearchBarProps) {
  return (
    <div className={`relative ${isRTL ? 'rtl' : ''}`}>
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary/30">
        <Search size={18} className="text-muted-foreground flex-shrink-0" />
        <input
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onFocus={onFocus}
          placeholder={
            language === 'fr'
              ? 'Rechercher...'
              : language === 'ar'
              ? 'بحث...'
              : 'Search...'
          }
          className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
        />
        {query && (
          <button
            onClick={onClear}
            className="p-1 rounded-full hover:bg-secondary/50 transition-colors"
            aria-label="Clear"
          >
            <X size={16} className="text-muted-foreground" />
          </button>
        )}
      </div>
    </div>
  )
}
